import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { isDeepEqual } from "remeda";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { LoadingButton } from "../../../../components/shared/LoadingButton";
import { useAddToPlan } from "../../../../hooks/react-query/useAddToPlan";
import { toast } from "../../../../utils/snackbarHelper";
import { toastMessage } from "../../../../constants/strings";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { SummaryContext } from "../../shared/ActionsMenuContext";
import {
  Appointment,
  AppointmentOld,
} from "../../../../services/prePlanningService.types";
import { Trap } from "../../../../types/planning/trap.type";
import ConfirmDialog from "../../../../components/shared/ConfirmDialog";
import { useIsWeekend } from "../../shared/useIsWeekend";
import { loadingButtonStyle } from "./StyledMenuButton";

interface AddToPlanActionButtonProps {
  selectedTrapOrBucket: Array<Trap | Appointment | AppointmentOld>;
  selectedRowsContext: SummaryContext[];
  clearSelection?: () => void;
}

const AddToPlanActionButton = ({
  selectedTrapOrBucket,
  selectedRowsContext,
  clearSelection,
}: AddToPlanActionButtonProps) => {
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const [serviceCenter] = useSelectedServiceCenter();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { dayName, isWeekend } = useIsWeekend();
  const selectedShipmentIds = selectedRowsContext.flatMap(
    (trapOrBucket) => trapOrBucket.shipmentIds
  );

  const isEnabledWhileSearching = useMemo(
    function enableWhileSearching() {
      return isDeepEqual(
        selectedRowsContext.flatMap((row) => row.shipmentIds),
        selectedShipmentIds
      );
    },
    [selectedRowsContext, selectedShipmentIds]
  );

  const addToPlan = useAddToPlan({
    date: dateToInt(selectedDate),
    sicId: serviceCenter.id,
    shipmentIds: selectedShipmentIds,
    onSuccess: (affectedShipmentsCount: number) => {
      clearSelection?.();
      toast(
        toastMessage.inbound.addToPlan.successAdding(affectedShipmentsCount),
        {
          variant: "success",
        }
      );
    },
  });

  const handleAddToPlan = () => {
    if (
      selectedRowsContext.every((r) => r.type === "bucket") ||
      selectedRowsContext.every((r) => r.type === "shipment")
    ) {
      return addToPlan.mutate();
    }

    if (selectedTrapOrBucket.length === 0 || !isEnabledWhileSearching) {
      toast(toastMessage.inbound.addToPlan.selectOne, {
        variant: "warning",
      });
    } else if (
      selectedTrapOrBucket.every(
        (trapOrBucket) => trapOrBucket.shipmentsCount === 0
      )
    ) {
      toast(toastMessage.inbound.addToPlan.emptyTrapSelected, {
        variant: "warning",
      });
    } else if (
      selectedTrapOrBucket.every(
        (trapOrBucket) => trapOrBucket.allShipmentsPlanned === true
      )
    ) {
      toast(toastMessage.inbound.addToPlan.plannedTrapSelected, {
        variant: "warning",
      });
    } else {
      addToPlan.mutate();
    }
  };

  const handleNo = () => setConfirmOpen(false);
  const handleYes = () => {
    setConfirmOpen(false);
    handleAddToPlan();
  };

  const handleClick = () => {
    if (isWeekend) {
      setConfirmOpen(true);
    } else {
      handleAddToPlan();
    }
  };

  return (
    <Box display="flex">
      <LoadingButton
        isDisabled={!isEnabledWhileSearching}
        isLoading={addToPlan.isPending}
        sx={loadingButtonStyle}
        color="secondary"
        onClick={handleClick}
        loadingLabel="ADDING..."
      >
        ADD TO PLAN
      </LoadingButton>
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleNo}
        title="Add to Plan"
        message={
          <>
            You are adding shipments to a plan on a <strong>{dayName}</strong>.
            Do you want to proceed?
          </>
        }
        cancelButtonText="No"
        confirmButtonLabel="Yes"
        confirmButtonLoadingLabel="Proceeding…"
        isConfirmPending={addToPlan.isPending}
        onConfirm={handleYes}
      />
    </Box>
  );
};

export default AddToPlanActionButton;
